import React, { useState, useEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, XCircle, Clock, Flag, Building2, FileText, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import contentModerationService from '../../services/contentModerationService';
import adminService from '../../services/adminService';
import Button from '../../components/ui/Button';

import Card from '../../components/ui/Card';
import { LoadingState } from '../../components/ui/LoadingStates';
import Icon from '../../components/AppIcon';


export default function AdminContentModerationDashboard() {
  const navigate = useNavigate();
  const authContext = useAuth();
  const { user = null } = authContext || {};
  const [adminProfile, setAdminProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('queue');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Moderation Queue State
  const [moderationQueue, setModerationQueue] = useState([]);
  const [queueFilters, setQueueFilters] = useState({
    status: '',
    priority: '',
    contentType: ''
  });

  // Content Flags State
  const [contentFlags, setContentFlags] = useState([]);
  const [flagFilters, setFlagFilters] = useState({
    resolved: false,
    contentType: '',
    flagReason: ''
  });

  // Company Verification State
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [verificationFilters, setVerificationFilters] = useState({
    status: 'pending'
  });

  // Statistics
  const [statistics, setStatistics] = useState(null);

  // Modals
  const [selectedItem, setSelectedItem] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'queue') {
      loadModerationQueue();
    } else if (activeTab === 'flags') {
      loadContentFlags();
    } else if (activeTab === 'verification') {
      loadVerificationRequests();
    }
  }, [activeTab, queueFilters, flagFilters, verificationFilters]);

  const loadDashboardData = async () => {
    if (!user) {
      navigate('/admin-authentication-portal');
      return;
    }

    try {
      setLoading(true);

      // Load admin profile
      const profile = await adminService?.getAdminProfile();
      setAdminProfile(profile);

      // Load statistics
      const stats = await contentModerationService?.getModerationStatistics();
      setStatistics(stats);

      // Load initial data
      await loadModerationQueue();
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadModerationQueue = async () => {
    try {
      const data = await contentModerationService?.getModerationQueue(queueFilters);
      setModerationQueue(data || []);
    } catch (err) {
      console.error('Error loading moderation queue:', err);
    }
  };

  const loadContentFlags = async () => {
    try {
      const data = await contentModerationService?.getContentFlags(flagFilters);
      setContentFlags(data || []);
    } catch (err) {
      console.error('Error loading content flags:', err);
    }
  };

  const loadVerificationRequests = async () => {
    try {
      const data = await contentModerationService?.getVerificationRequests(verificationFilters);
      setVerificationRequests(data || []);
    } catch (err) {
      console.error('Error loading verification requests:', err);
    }
  };

  const handleReviewAction = async () => {
    if (!selectedItem || !reviewAction) return;

    try {
      setActionLoading(true);

      if (activeTab === 'queue') {
        if (reviewAction === 'approve') {
          await contentModerationService?.approveContent(selectedItem?.id, reviewNotes);
        } else if (reviewAction === 'reject') {
          await contentModerationService?.rejectContent(selectedItem?.id, reviewNotes);
        }
        await loadModerationQueue();
      } else if (activeTab === 'verification') {
        if (reviewAction === 'approve') {
          await contentModerationService?.verifyCompany(selectedItem?.id, 'verified', reviewNotes);
        } else if (reviewAction === 'reject') {
          await contentModerationService?.rejectCompanyVerification(selectedItem?.id, reviewNotes);
        }
        await loadVerificationRequests();
      } else if (activeTab === 'flags') {
        await contentModerationService?.resolveFlag(selectedItem?.id, reviewNotes);
        await loadContentFlags();
      }

      // Reload statistics
      const stats = await contentModerationService?.getModerationStatistics();
      setStatistics(stats);

      // Close modal
      setShowReviewModal(false);
      setSelectedItem(null);
      setReviewAction('');
      setReviewNotes('');
    } catch (err) {
      console.error('Error performing review action:', err);
      alert(err?.message || 'Failed to perform action');
    } finally {
      setActionLoading(false);
    }
  };

  const openReviewModal = (item, action) => {
    setSelectedItem(item);
    setReviewAction(action);
    setShowReviewModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      under_review: 'bg-blue-100 text-blue-800 border-blue-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
      flagged: 'bg-orange-100 text-orange-800 border-orange-300',
      verified: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600'
    };
    return colors?.[priority] || colors?.medium;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/admin-operations-dashboard')} className="w-full">
            Return to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin-operations-dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Dashboard
              </Button>
              <div className="h-8 w-px bg-gray-300"></div>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-purple-600 rounded-lg">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Content Moderation</h1>
                  <p className="text-sm text-gray-500">Review and moderate platform content</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{adminProfile?.user?.fullName}</p>
                <span className="text-xs text-gray-500">{adminProfile?.adminRole}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Statistics Cards */}
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Clock}
            label="Pending Review"
            value={statistics?.pendingItems || 0}
            color="yellow"
          />
          <StatCard
            icon={Flag}
            label="Flagged Items"
            value={statistics?.flaggedItems || 0}
            color="orange"
          />
          <StatCard
            icon={AlertTriangle}
            label="Urgent Items"
            value={statistics?.urgentItems || 0}
            color="red"
          />
          <StatCard
            icon={Building2}
            label="Pending Verifications"
            value={statistics?.pendingVerifications || 0}
            color="blue"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <div className="flex gap-1 p-2">
              <TabButton
                active={activeTab === 'queue'}
                onClick={() => setActiveTab('queue')}
                icon={FileText}
                label="Moderation Queue"
                count={statistics?.pendingItems + statistics?.underReview}
              />
              <TabButton
                active={activeTab === 'flags'}
                onClick={() => setActiveTab('flags')}
                icon={Flag}
                label="Content Flags"
                count={statistics?.unresolvedFlags}
              />
              <TabButton
                active={activeTab === 'verification'}
                onClick={() => setActiveTab('verification')}
                icon={Building2}
                label="Company Verification"
                count={statistics?.pendingVerifications}
              />
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'queue' && (
              <ModerationQueueTab
                queue={moderationQueue}
                filters={queueFilters}
                setFilters={setQueueFilters}
                onReview={openReviewModal}
                getStatusColor={getStatusColor}
                getPriorityColor={getPriorityColor}
              />
            )}

            {activeTab === 'flags' && (
              <ContentFlagsTab
                flags={contentFlags}
                filters={flagFilters}
                setFilters={setFlagFilters}
                onResolve={openReviewModal}
                getStatusColor={getStatusColor}
              />
            )}

            {activeTab === 'verification' && (
              <CompanyVerificationTab
                requests={verificationRequests}
                filters={verificationFilters}
                setFilters={setVerificationFilters}
                onReview={openReviewModal}
                getStatusColor={getStatusColor}
              />
            )}
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          item={selectedItem}
          action={reviewAction}
          notes={reviewNotes}
          setNotes={setReviewNotes}
          onConfirm={handleReviewAction}
          onCancel={() => {
            setShowReviewModal(false);
            setSelectedItem(null);
            setReviewAction('');
            setReviewNotes('');
          }}
          loading={actionLoading}
        />
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600'
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses?.[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
}

// Tab Button Component
function TabButton({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
        active
          ? 'bg-blue-50 text-blue-600 border border-blue-200' :'text-gray-600 hover:bg-gray-50'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
      {count > 0 && (
        <span className={`px-2 py-0.5 text-xs rounded-full ${
          active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

// Moderation Queue Tab
function ModerationQueueTab({ queue, filters, setFilters, onReview, getStatusColor, getPriorityColor }) {
  return (
    <div>
      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <select
          value={filters?.status}
          onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="under_review">Under Review</option>
          <option value="flagged">Flagged</option>
        </select>

        <select
          value={filters?.priority}
          onChange={(e) => setFilters({ ...filters, priority: e?.target?.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>

        <select
          value={filters?.contentType}
          onChange={(e) => setFilters({ ...filters, contentType: e?.target?.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All Types</option>
          <option value="job_posting">Job Posting</option>
          <option value="company_profile">Company Profile</option>
        </select>
      </div>

      {/* Queue Items */}
      <div className="space-y-3">
        {queue?.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-gray-600">No items in moderation queue</p>
          </div>
        ) : (
          queue?.map((item) => (
            <Card key={item?.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(item?.status)}`}>
                      {item?.status?.replace('_', ' ')?.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(item?.priority)}`}>
                      {item?.priority?.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {item?.contentType?.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Submitted by: <span className="font-medium">{item?.submittedBy?.fullName || 'Unknown'}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(item?.createdAt)?.toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReview(item, 'approve')}
                    className="text-green-600 hover:bg-green-50"
                  >
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReview(item, 'reject')}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <ThumbsDown className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

// Content Flags Tab
function ContentFlagsTab({ flags, filters, setFilters, onResolve, getStatusColor }) {
  return (
    <div>
      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <select
          value={filters?.resolved ? 'true' : 'false'}
          onChange={(e) => setFilters({ ...filters, resolved: e?.target?.value === 'true' })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="false">Unresolved</option>
          <option value="true">Resolved</option>
        </select>

        <select
          value={filters?.flagReason}
          onChange={(e) => setFilters({ ...filters, flagReason: e?.target?.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All Reasons</option>
          <option value="spam">Spam</option>
          <option value="inappropriate_content">Inappropriate Content</option>
          <option value="misleading_information">Misleading Information</option>
          <option value="policy_violation">Policy Violation</option>
        </select>
      </div>

      {/* Flag Items */}
      <div className="space-y-3">
        {flags?.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-gray-600">No content flags found</p>
          </div>
        ) : (
          flags?.map((flag) => (
            <Card key={flag?.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Flag className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-gray-900">
                      {flag?.flagReason?.replace('_', ' ')?.toUpperCase()}
                    </span>
                    {flag?.isAutomated && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">
                        Automated
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{flag?.flagDetails}</p>
                  <p className="text-xs text-gray-500">
                    Flagged by: {flag?.flaggedBy?.fullName || 'System'} • {new Date(flag?.createdAt)?.toLocaleString()}
                  </p>
                </div>
                {!flag?.resolved && (
                  <Button
                    size="sm"
                    onClick={() => onResolve(flag, 'resolve')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Resolve
                  </Button>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

// Company Verification Tab
function CompanyVerificationTab({ requests, filters, setFilters, onReview, getStatusColor }) {
  return (
    <div>
      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <select
          value={filters?.status}
          onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="pending">Pending</option>
          <option value="under_review">Under Review</option>
          <option value="verified">Verified</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Verification Requests */}
      <div className="space-y-3">
        {requests?.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No verification requests found</p>
          </div>
        ) : (
          requests?.map((request) => (
            <Card key={request?.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    {request?.company?.logo_url && (
                      <img
                        src={request?.company?.logo_url}
                        alt={request?.businessName}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{request?.businessName}</h3>
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(request?.status)}`}>
                        {request?.status?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">Registration Number</p>
                      <p className="font-medium">{request?.businessRegistrationNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Website</p>
                      <p className="font-medium">{request?.websiteUrl || 'N/A'}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Requested: {new Date(request?.createdAt)?.toLocaleString()}
                  </p>
                </div>
                {request?.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReview(request, 'approve')}
                      className="text-green-600 hover:bg-green-50"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Verify
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReview(request, 'reject')}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

// Review Modal
function ReviewModal({ item, action, notes, setNotes, onConfirm, onCancel, loading }) {
  const getActionText = () => {
    if (action === 'approve') return 'Approve';
    if (action === 'reject') return 'Reject';
    if (action === 'resolve') return 'Resolve';
    return 'Confirm';
  };

  const getActionColor = () => {
    if (action === 'approve') return 'bg-green-600 hover:bg-green-700';
    if (action === 'reject') return 'bg-red-600 hover:bg-red-700';
    return 'bg-blue-600 hover:bg-blue-700';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          {getActionText()} Item
        </h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {action === 'reject' ? 'Rejection Reason' : 'Review Notes'}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e?.target?.value)}
            placeholder={action === 'reject' ? 'Please provide a reason for rejection...' : 'Add any notes about this review...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={4}
            required={action === 'reject'}
          />
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onCancel}
            variant="outline"
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className={`flex-1 ${getActionColor()}`}
            disabled={loading || (action === 'reject' && !notes)}
          >
            {loading ? 'Processing...' : getActionText()}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}